import { useQueryStates } from 'nuqs';
import { useCallback } from 'react';
import {
  clearContactParams,
  contactDrawerParsers,
  openContactParams,
  type ContactEntry,
  type ContactPlatform,
} from '@/components/contact/contact-drawer-url-state';
import type { ContactView } from '@/components/contact/contact-views';

/**
 * A reference to the contact to open. The drawer resolves these to a single
 * creator identity id via the `/dashboard/contacts/resolve` endpoint
 * (affiliate: userId → identity, creator: username → identity, identity:
 * already the identity id — the resolve still runs for the header + the brand
 * authorization gate).
 */
export type ContactRef =
  | { userId: string }
  | { username: string; sourceCreatorId?: string }
  | { identityId: string }
  /** A prospect the plan masks: no handle to resolve, only its dataset reference. */
  | { sourceCreatorId: string };

export interface OpenContactOptions {
  view?: ContactView;
  platform?: ContactPlatform;
  collab?: string | null;
  deliverable?: string | null;
}

/**
 * Derive a stable, opaque `contactId` from whichever ref shape was passed.
 * Deterministic so reopening the same contact reuses the same URL and the
 * drawer/view tree stays addressable.
 */
const resolveContactId = (ref: ContactRef): string => {
  if ('userId' in ref) return ref.userId;
  if ('identityId' in ref) return ref.identityId;
  if ('username' in ref) return ref.username;
  return ref.sourceCreatorId;
};

/**
 * The entry discriminant matching the ref branch, stashed in URL state so the
 * drawer can build the resolve ref `{ entryType, id }`.
 */
const resolveContactEntry = (ref: ContactRef): ContactEntry => {
  if ('userId' in ref) return 'affiliate';
  if ('identityId' in ref) return 'identity';
  if ('username' in ref) return 'creator';
  return 'source';
};

/**
 * The source dataset creator id, carried on the `username` (creator) and the
 * `sourceCreatorId` (masked prospect) refs so the "save this creator" action
 * can add it to the wishlist. Null for affiliate and identity refs, which are
 * already saved contacts.
 */
const resolveSourceCreatorId = (ref: ContactRef): string | null => {
  if ('username' in ref) return ref.sourceCreatorId ?? null;
  if ('sourceCreatorId' in ref) return ref.sourceCreatorId;
  return null;
};

/**
 * Opens or closes the global contact drawer from anywhere via URL state.
 *
 * `open(ref, opts?)` writes the `contact`-prefixed params (which mounts the
 * single global drawer); `close()` clears them all.
 */
export const useOpenContactDrawer = () => {
  const [, setParams] = useQueryStates(contactDrawerParsers);

  const open = useCallback(
    (ref: ContactRef, opts?: OpenContactOptions) => {
      void setParams(
        openContactParams({
          contactId: resolveContactId(ref),
          entry: resolveContactEntry(ref),
          view: opts?.view,
          platform: opts?.platform,
          collab: opts?.collab,
          deliverable: opts?.deliverable,
          sourceCreatorId: resolveSourceCreatorId(ref),
        })
      );
    },
    [setParams]
  );

  const close = useCallback(() => {
    void setParams(clearContactParams());
  }, [setParams]);

  return { open, close };
};
